import { runtimeConfig } from "@/runtime-config";
import { assert } from "@faro-lotv/foundation";

/**
 * Download a file
 *
 * @param url The url to the file to download
 * @param name Optional name of the downloaded file
 * @param newTab Setting this to true will download the file in a new tab, keeping the content of the current tab.
 *  This is helpful for downloads that the browser would otherwise open in the same tab (e.g., PDF files).
 */
export function downloadFile(
  url: string,
  name?: string,
  newTab?: boolean,
): void {
  const anchor = document.createElement("a");
  anchor.href = url;
  if (name) anchor.download = name;
  if (newTab) anchor.target = "_blank";

  anchor.click();
  anchor.remove();
}

/**
 * Downloads a file from a given URL and prompts the browser to save it.
 *
 * This function addresses browser behavior (notably in Chrome) where certain file types, such as images or PDFs,
 * are opened in the browser instead of being downloaded. It fetches the file as a Blob and triggers a download,
 * ensuring the file is saved with the specified filename.
 *
 * @param url - The URL of the file to download.
 * @param filename - The desired filename for the downloaded file.
 * @returns A promise that resolves when the download is triggered.
 */
export async function downloadFileAsBlob(
  url: string,
  filename: string,
): Promise<void> {
  const response = await fetch(url, { mode: "cors" });
  const blob = await response.blob();

  // Create a blob URL to trigger browser download.
  const blobUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");

  anchor.href = blobUrl;
  anchor.download = filename;
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);

  // Clean up the blob URL to avoid memory leaks.
  URL.revokeObjectURL(blobUrl);
}

/** Minimum file size for using the download manager (1 GB) */
const MIN_SIZE_FOR_DOWNLOAD_MANAGER = 1 * 1024 * 1024 * 1024;

/**
 * Downloads a large file. First checks if the file size exceeds the threshold for using the download manager.
 * If it does, it opens the download manager in a new tab. Otherwise, it proceeds with a normal download.
 *
 * @param signedUrl The signed URL to download.
 * @param filename - The desired filename for the downloaded image.
 */
export async function downloadLargeFile(
  signedUrl: string,
  filename?: string,
): Promise<void> {
  const contentLength = await getContentLength(signedUrl);
  if (contentLength !== null && contentLength < MIN_SIZE_FOR_DOWNLOAD_MANAGER) {
    downloadFile(signedUrl, filename);
  } else {
    downloadFileWithDownloadManager(signedUrl);
  }
}

/**
 * @returns the content length of a URI resource, or null if it cannot be determined.
 * @param url The URL of the resource.
 */
async function getContentLength(url: string): Promise<number | null> {
  try {
    const response = await fetch(url, { method: "HEAD" });
    assert(response.ok, `Failed to fetch headers for ${url}`);

    const contentLength = response.headers.get("Content-Length");
    assert(contentLength, `Content-Length header is missing for ${url}`);

    const parsed = parseInt(contentLength, 10);
    assert(!isNaN(parsed), `Content-Length header is NaN for ${url}`);

    return parsed;
  } catch {
    return null;
  }
}

/**
 * Downloads a file using the Sphere XG download manager web app, which provides a more robust download experience for large files.
 * Use this function only for files that are expected to be large > 1GB. Otherwise the normal browser download should be sufficient.
 *
 * @param signedUrl The signed URL to download.
 */
export function downloadFileWithDownloadManager(signedUrl: string): void {
  window.open(
    new URL(
      `?url=${encodeURIComponent(signedUrl)}`,
      runtimeConfig.externalLinks.downloaderUrl,
    ),
    "_blank",
  );
}
